import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import { Plus, CheckCircle, XCircle } from 'lucide-react'

interface Carrier {
  id: number
  name: string
  code: string
  is_active: boolean
  commission_count: number
  created_at: string
}

interface Props extends PageProps {
  carriers: Carrier[]
}

export default function CarriersIndex({ carriers }: Props) {
  const columns: Column<Carrier>[] = [
    {
      key: 'name',
      header: 'Carrier Name',
      sortable: true,
      render: (c) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{c.name}</div>
          <div className="text-xs text-chestnut-charcoal/60">Code: {c.code}</div>
        </div>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          {c.is_active ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {c.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'commission_count',
      header: 'Commissions',
      sortable: true,
      className: 'text-center'
    }
  ]

  return (
    <AppLayout>
      <Head title="Carriers" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Carriers</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Manage insurance carriers and commission split schedules</p>
          </div>
          <Link href="/carriers/new" className="btn-primary flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Carrier
          </Link>
        </div>

        <div className="card p-6">
          <DataTable
            data={carriers}
            columns={columns}
            searchPlaceholder="Search carriers..."
            searchKeys={['name', 'code']}
            pageSize={10}
            emptyMessage="No carriers yet. Add your first carrier to get started."
            getRowKey={(c) => c.id}
            onRowClick={(c) => router.visit(`/carriers/${c.id}`)}
          />
        </div>
      </div>
    </AppLayout>
  )
}
